import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * A lazy page whose chunk no longer exists (portal redeployed while the tab was
 * open) or could not be downloaded (network drop). React caches the rejected
 * import, so "Try again" cannot recover it; only a full reload fetches the new
 * index.html and its chunk names.
 */
export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? '');
  return /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Expected a JavaScript.*module script/i.test(
    message,
  );
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      const chunkError = isChunkLoadError(this.state.error);
      return (
        <div className="flex flex-col items-center justify-center gap-4 p-8">
          <h2 className="text-lg font-semibold text-destructive">
            {chunkError ? 'A new version of the portal is available' : 'Something went wrong'}
          </h2>
          <p className="text-sm text-muted-foreground">
            {chunkError ? 'Reload the page to continue.' : this.state.error?.message}
          </p>
          <button
            onClick={() =>
              chunkError ? window.location.reload() : this.setState({ hasError: false, error: null })
            }
            className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground hover:bg-primary/90"
          >
            {chunkError ? 'Reload' : 'Try again'}
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
